import { Modifier, Placement } from "@popperjs/core";
import { Options as ArrowModiferOptions } from "@popperjs/core/lib/modifiers/arrow";
import { usePrevious } from "@react-hookz/web";
import { ThemeKey } from "@smartsheet/design-tokens";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import { Omit } from "utility-types";
import { useTheme } from "../../theme/utils/useTheme";
import { Flyout, FlyoutProps } from "../flyout";
import { defaultArrowSize } from "../flyout/styled/FlyoutArrow";
import { TooltipFlyoutArrow } from "./components/TooltipFlyoutArrow";
import { TooltipFlyoutContainer, TooltipFlyoutContainerProps } from "./components/TooltipFlyoutContainer";
import { ANIMATION_DURATION, tooltipFlyoutDefaults } from "./tooltipConstants";
import { tooltipTheme } from "./tooltipTheme";
import { ArrowEdge, DirectionOrAuto } from "./types";

export type TooltipProps = Omit<FlyoutProps, "onCloseRequested" | "components">;

/**
 * For each placement, we need to figure out if we should square off a corner of the flyout
 * to mesh well with the arrow when it's at the edge.
 * @param placement
 */
function placementToSquaredCorner(placement: Placement): TooltipFlyoutContainerProps["squareCorner"] {
    switch (placement) {
        case "auto":
        case "auto-end":
        case "auto-start":
            return undefined;
        case "top-start":
        case "left-start":
            return "borderBottomRight";
        case "top-end":
        case "right-start":
            return "borderBottomLeft";
        case "right-end":
        case "bottom-end":
            return "borderTopLeft";
        case "bottom-start":
        case "left-end":
            return "borderTopRight";
        default:
            return undefined;
    }
}

function getArrowEdge(placement: Placement, offset: number): ArrowEdge {
    if (offset === 0) {
        return false;
    }
    switch (placement) {
        case "auto":
        case "auto-end":
        case "auto-start":
            return false;
        case "top-start":
        case "top":
        case "top-end":
        case "bottom-start":
        case "bottom":
        case "bottom-end":
            return offset > 0 ? "left" : "right";
        case "right-start":
        case "right":
        case "right-end":
        case "left-start":
        case "left":
        case "left-end":
            return offset > 0 ? "top" : "bottom";
        default:
            return false;
    }
}

/**
 * Basic tooltip container.
 * Adds a standard backdrop to the Flyout and should be combined with an
 * inner wrapper like TooltipBody to provide the correct spacing.
 */
export const Tooltip: FC<TooltipProps> = (props) => {
    const { key } = useTheme();
    const themeMode = key as ThemeKey;

    const arrowAtEdge = useRef<"top" | "left" | "right" | "bottom" | false>(false);

    // If auto, the actual placement can be different with different target dimensions and/or scroll positions.
    const actualPlacement = useRef<Placement>(props.placement || "auto");
    // Initially do not transition until this is true.
    const [showTransition, setShowTransition] = useState(false);
    const showTransitionRafRef = useRef<number>();

    // Clean this up so we aren't setting state on unmounted components.
    useEffect(
        () => () => {
            if (showTransitionRafRef.current) {
                cancelAnimationFrame(showTransitionRafRef.current);
            }
        },
        []
    );

    // Adds a write-phase modifer to check if the arrow is going to be rendered at the edge of the
    // Flyout's content. If so, we want to render a different arrow shape.
    const arrowEdgeModifier: Modifier<"arrowAtEdge", ArrowModiferOptions> = useMemo(
        () => ({
            name: "arrowAtEdge",
            enabled: true,
            phase: "write",
            fn({ state }) {
                if (state.elements.arrow && state.modifiersData.arrow) {
                    actualPlacement.current = state.placement;
                    if (
                        state.modifiersData.arrow.centerOffset !== undefined &&
                        Math.abs(state.modifiersData.arrow.centerOffset) > 0
                    ) {
                        arrowAtEdge.current = getArrowEdge(state.placement, state.modifiersData.arrow.centerOffset);
                    } else {
                        arrowAtEdge.current = false;
                    }
                }
            },
        }),
        []
    );

    const enterDirection = actualPlacement.current.split("-")[0] as DirectionOrAuto;
    const { isOpen } = props;
    const previousIsOpen = usePrevious(isOpen);
    const showTooltipArrow = props.hasArrow || (props.hasArrow === undefined && tooltipFlyoutDefaults.hasArrow);

    return (
        <Transition
            // The tooltip should animate on mount so this is always true.
            in={isOpen}
            timeout={{ enter: 0, exit: ANIMATION_DURATION }}
            appear
        >
            {(transitionStatus: TransitionStatus) => {
                return (
                    <Flyout
                        {...tooltipFlyoutDefaults}
                        role="tooltip"
                        {...props}
                        // We want to remove this from the stack order so it doesn't interfere with
                        // other floating elements participating in a StackOrderContext. When a
                        // Tooltip opens on top of another Floating element, like a Flyout, it would
                        // be considered the top-most element. Since you cannot close the Tooltip
                        // with Escape (currently), Escape would be swallowed up by the Tooltip and
                        // nothing would happen. If we want to allow Escape to close the Tooltip, we
                        // can remove this line, but we MUST then teach the Tooltip how to close
                        // itself until it's requested to open again.
                        ignoreStackOrder
                        popperProps={{
                            ...props.popperProps,
                            modifiers: [arrowEdgeModifier, ...(props.popperProps?.modifiers || [])],
                            onFirstUpdate: (args) => {
                                if (typeof props.popperProps?.onFirstUpdate === "function") {
                                    props.popperProps.onFirstUpdate(args);
                                }
                                showTransitionRafRef.current = requestAnimationFrame(() => {
                                    setShowTransition(true);
                                });
                            },
                        }}
                        // Once isOpen is set to false, we want to keep it rendered until the exit animation completes.
                        isOpen={isOpen || (!!previousIsOpen && !isOpen && transitionStatus !== "exited")}
                    >
                        {({ getArrowProps }) => (
                            <TooltipFlyoutContainer
                                enterDirection={enterDirection}
                                transitionStatus={transitionStatus}
                                showTransition={showTransition}
                                offset={
                                    props.popperProps?.modifiers?.find((m) => m.name === "offset")?.options?.offset as [
                                        number,
                                        number
                                    ]
                                }
                                squareCorner={
                                    (arrowAtEdge && placementToSquaredCorner(actualPlacement.current)) || undefined
                                }
                            >
                                {props.children}
                                {showTooltipArrow && (
                                    <TooltipFlyoutArrow
                                        {...getArrowProps()}
                                        backgroundColor={
                                            props.arrowColor ||
                                            `var(--sds--tooltip--background-color, ${tooltipTheme.background(
                                                themeMode
                                            )})`
                                        }
                                        arrowAtEdge={arrowAtEdge.current}
                                        size={defaultArrowSize}
                                        placement={actualPlacement.current}
                                    />
                                )}
                            </TooltipFlyoutContainer>
                        )}
                    </Flyout>
                );
            }}
        </Transition>
    );
};
